<template>
  <div class="px-6 py-4 section-empty-state">
    <div class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-8 text-center border border-slate-200 shadow-sm">
      <!-- Icon Container -->
      <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
        <!-- Section-specific Icons -->
        
        <!-- Popular / Hot (Flame) -->
        <div v-if="iconType === 'popular'" class="text-slate-500">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.99 7.99 0 0120 13a7.98 7.98 0 01-2.343 5.657z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.879 16.121A3 3 0 1012.015 11L11 14l2.879 2.121z" />
          </svg>
        </div>

        <!-- New / Recent (Magic/Stars) -->
        <div v-else-if="iconType === 'new'" class="text-slate-500">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-7.714 2.143L11 21l-2.286-6.857L1 12l7.714-2.143L11 3z" />
          </svg>
        </div>

        <!-- Near You (Location/Map) -->
        <div v-else-if="iconType === 'near' || iconType === 'near-you'" class="text-slate-500">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>

        <!-- Recommendations (Diamond/Magic) -->
        <div v-else-if="iconType === 'recommendations' || iconType === 'recs'" class="text-slate-500">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        </div>

        <!-- Deals / Coupons (Tag/Ticket) -->
        <div v-else-if="iconType === 'deals' || iconType === 'coupons'" class="text-slate-500">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        </div>

        <!-- Default (Shop/Building) -->
        <div v-else class="text-slate-500">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
      </div>

      <!-- Text Content -->
      <h3 class="text-lg font-bold text-slate-800 mb-2">
        {{ title || 'Bientôt disponible' }}
      </h3>
      <p class="text-slate-500 text-sm mb-6 max-w-[240px] mx-auto">
        {{ description || 'Revenez bientôt pour découvrir de nouvelles offres !' }}
      </p>

      <!-- Refresh Button -->
      <button
        v-if="showRefresh"
        @click="$emit('refresh')"
        class="flex items-center gap-2 mx-auto px-5 py-2 bg-white text-slate-700 font-bold rounded-full border border-slate-200 shadow-sm active:scale-95 transition-all"
      >
        <svg class="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m0 0H15" />
        </svg>
        Rafraîchir
      </button>

      <!-- Loading Indicator (Optional) -->
      <div v-if="loading" class="mt-4 flex justify-center">
        <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  title: String,
  description: String,
  iconType: String,
  showRefresh: {
    type: Boolean,
    default: true
  },
  loading: Boolean
});

defineEmits(['refresh']);
</script>

<style scoped>
.section-empty-state {
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
